/**
 * Normalize Django/DRF API error bodies into user-visible message lines.
 */

/**
 * @param {*} value
 * @returns {string[]}
 */
function linesFromValue(value) {
  if (value == null) return [];
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  if (Array.isArray(value)) {
    return value.flatMap(v => linesFromValue(v));
  }
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return [value.message];
    if (typeof value.detail === 'string') return [value.detail];
    return [JSON.stringify(value)];
  }
  return [String(value)];
}

/**
 * Collect messages from a flat or nested error object (skips known wrapper keys when recursing).
 *
 * @param {Object} obj
 * @param {string} [prefix]
 * @returns {string[]}
 */
function linesFromObject(obj, prefix = '') {
  const messages = [];
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return messages;

  if (Array.isArray(obj.non_field_errors)) {
    messages.push(...obj.non_field_errors.flatMap(v => linesFromValue(v)));
  }

  Object.entries(obj).forEach(([key, val]) => {
    if (key === 'non_field_errors' || key === 'success' || key === 'data') return;

    if (key === 'details' && val && typeof val === 'object') {
      messages.push(...linesFromObject(val, prefix));
      return;
    }

    const label = ['error', 'message', 'detail'].includes(key)
      ? ''
      : prefix
        ? `${prefix}${key}: `
        : `${key}: `;

    if (Array.isArray(val)) {
      val.forEach(v => {
        linesFromValue(v).forEach(line => messages.push(`${label}${line}`));
      });
    } else if (typeof val === 'string') {
      messages.push(`${label}${val}`);
    } else if (val && typeof val === 'object') {
      if (key === 'error' || key === 'message' || key === 'detail') {
        messages.push(...linesFromValue(val));
      } else {
        messages.push(...linesFromObject(val, label));
      }
    }
  });

  return messages;
}

/**
 * @param {*} errorData
 * @param {string} fallback
 * @returns {string[]}
 */
export function formatApiErrorLines(errorData, fallback) {
  if (errorData == null) return [fallback];

  if (typeof errorData === 'string') {
    const t = errorData.trim();
    return t ? [t] : [fallback];
  }

  if (Array.isArray(errorData)) {
    const lines = errorData.flatMap(v => linesFromValue(v));
    return lines.length ? lines : [fallback];
  }

  const lines = linesFromObject(errorData);
  const seen = new Set();
  const unique = lines.filter(line => {
    const key = line.trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return unique.length ? unique : [fallback];
}

/**
 * @param {*} errorData
 * @param {string} fallback
 * @returns {string}
 */
export function formatApiErrorMessage(errorData, fallback) {
  return formatApiErrorLines(errorData, fallback).join('\n');
}

/**
 * @param {*} errorData
 * @param {string} fallback
 * @param {number|null} [httpStatus]
 * @returns {{ message: string, lines: string[], status: number|null }}
 */
export function parseApiErrorResponse(errorData, fallback, httpStatus = null) {
  const lines = formatApiErrorLines(errorData, fallback);
  let message = lines.join('\n');

  if (
    httpStatus != null &&
    lines.length === 1 &&
    lines[0] === fallback
  ) {
    message = `${fallback} (HTTP ${httpStatus})`;
    lines[0] = message;
  }

  return {
    message,
    lines,
    status: httpStatus ?? null,
  };
}
